/**
 * Один глобальный инстанс Laravel Echo (Socket.IO) для чата.
 * Создаётся один раз при первой подписке, отключается при логауте.
 */
// @ts-expect-error laravel-echo has no types; Echo constructor accepts the config below
import Echo from 'laravel-echo';
import io, { type Socket } from 'socket.io-client';
import LocalStorageHelper from '#app/helpers/Cache/LocalStorageHelper';

const RECONNECT_DELAY_MS = 5000;
const RECONNECT_ATTEMPTS = 3;

let echo: any = null;

function getSocketHost(): string | undefined {
  return process.env.REACT_APP_SOCKET_HOST;
}

function getSocketPort(): string {
  const port = process.env.REACT_APP_SOCKET_PORT;
  return port != null && port !== '' ? port : '6001';
}

function getAuthHeaders(userId: string | number): Record<string, string> {
  const apiToken = localStorage.getItem(LocalStorageHelper.API_TOKEN) || '';
  const env = process.env.REACT_APP_ENV ?? '';
  return {
    Authorization: apiToken,
    UserId: env + '-' + String(userId),
    'Content-Type': 'application/json',
  };
}

function getAuthEndpoint(): string {
  const host = getSocketHost();
  return host ? `https://${host}/broadcasting/auth` : '';
}

/**
 * Создаёт и возвращает глобальный Echo (один раз при !echo).
 * Условие: io есть, env = production/testing, задан REACT_APP_SOCKET_HOST.
 */
export function connectWebsocket(userId: string | number, successCallback: () => void): void {
  const host = getSocketHost();
  const env = process.env.REACT_APP_ENV ?? '';
  const isAllowedEnv = env === 'production' || env === 'testing';

  if (!io || !isAllowedEnv || !host) {
    return;
  }

  if (echo) {
    const socket = (echo as any).connector?.socket as Socket | undefined;
    if (socket?.connected) {
      successCallback();
      return;
    }
    if (socket) {
      socket.connect();
      const onConnect = () => {
        socket.off('connect', onConnect);
        successCallback();
      };
      socket.once('connect', onConnect);
      return;
    }
  }

  const port = getSocketPort();
  // wss: явный REACT_APP_SOCKET_TLS=wss, или https-страница, или хост на whiteswan.finance (сервер принимает только TLS)
  const envScheme = process.env.REACT_APP_SOCKET_TLS;
  const isRemoteSecureHost = /whiteswan\.finance$/i.test(host);
  const useWss =
    envScheme === 'wss' ||
    envScheme === 'https' ||
    (typeof window !== 'undefined' && window.location?.protocol === 'https:') ||
    isRemoteSecureHost;
  const scheme = useWss ? 'wss' : 'ws';
  const socketHost = `${scheme}://${host}:${port}`;

  echo = new Echo({
    broadcaster: 'socket.io',
    host: socketHost,
    client: io,
    secure: useWss,
    auth: {
      headers: getAuthHeaders(userId),
    },
    authEndpoint: getAuthEndpoint(),
    transports: useWss ? ['websocket'] : ['websocket', 'polling'],
    reconnection: true,
    reconnectionDelay: RECONNECT_DELAY_MS,
    reconnectionAttempts: RECONNECT_ATTEMPTS,
  });

  const socket = (echo as any).connector?.socket as Socket | undefined;
  if (socket) {
    socket.on('connect', () => {
      console.log('[WebSocket] connect callback ran (socket connected)');
      successCallback();
    });
    socket.on('disconnect', () => {
      setTimeout(() => {
        if (socket && !socket.connected) {
          socket.connect();
        }
      }, RECONNECT_DELAY_MS);
    });
    socket.on('connect_error', (err: Error) => {
      console.warn('[WebSocket] connect_error', err?.message ?? err);
    });
    socket.on('error', (err: Error) => {
      console.warn('[WebSocket] error', err?.message ?? err);
    });
    socket.on('reconnect_error', (err: Error) => {
      console.warn('[WebSocket] reconnect_error', err?.message ?? err);
    });
    socket.on('reconnect_failed', () => {
      console.warn('[WebSocket] reconnect_failed');
    });
    socket.on('reconnect', () => {
      // optional: можно вызвать successCallback для переподписки pending-каналов
    });
  } else {
    successCallback();
  }
}

/**
 * Отключение и сброс глобального Echo. Вызывать при логауте.
 */
export function disconnectWebSocket(): void {
  if (!echo) return;
  const socket = (echo as any).connector?.socket as Socket | undefined;
  if (socket) {
    socket.removeAllListeners();
    socket.disconnect();
  }
  if (typeof (echo as any).disconnect === 'function') {
    (echo as any).disconnect();
  }
  echo = null;
}

export function getEcho(): any {
  return echo;
}

/**
 * Отправка «печатает» в канал чата (client whisper).
 * Событие получат другие подписчики канала (listenForWhisper).
 */
export function emitChatTyping(chatId: string, isTyping: boolean): void {
  if (!chatId || !echo) return;
  const channelName = `chat-${chatId}`;
  try {
    const ch = (echo as any).private(channelName);
    if (ch?.whisper) {
      ch.whisper('typing', { typing: isTyping });
    }
  } catch {
    // ignore
  }
}
